import { FC } from "react";
import { Button, Card, Group, Text, LoadingOverlay } from "@mantine/core";
import { IconFileTypePdf, IconZoomIn } from "@tabler/icons-react";
import { UserData } from "../../../../types/org.type";
import { RegtankPassportPhoto } from "../hooks/useRegtankPhotos";
import ZoomOverlay from "./ZoomOverlay";
import FileUploadButton from "./FileUploadButton";

export interface LocalPassportPhoto {
  file: File;
  previewUrl: string;
  binaryData?: ArrayBuffer;
}

// --- Local sub-components to eliminate duplicate JSX patterns ---

const PdfPreviewCard: FC<{
  displayName: string;
  dataUrl: string;
  border?: string;
  width?: string;
  minHeight?: string;
  onPreview: () => void;
}> = ({
  displayName,
  dataUrl,
  border = "1px solid #ddd",
  width,
  minHeight,
  onPreview,
}) => (
  <div
    style={{
      display: "flex",
      flexDirection: "column",
      alignItems: "center",
      justifyContent: "center",
      padding: "20px",
      backgroundColor: "#f5f5f5",
      borderRadius: "8px",
      border,
      cursor: "pointer",
      position: "relative",
      width,
      minHeight,
    }}
    onClick={onPreview}
  >
    <IconFileTypePdf size={48} color="#e53935" />
    <Text
      size="sm"
      mt="xs"
      align="center"
      style={{ maxWidth: "200px", wordBreak: "break-word" }}
    >
      {displayName}
    </Text>
    <Button
      variant="light"
      size="xs"
      mt="sm"
      leftIcon={<IconZoomIn size={14} />}
      onClick={(e) => {
        e.stopPropagation();
        onPreview();
      }}
    >
      View PDF
    </Button>
  </div>
);

const ClickablePhoto: FC<{
  src: string;
  alt: string;
  className: string;
  dimmed?: boolean;
  zoomPosition?: "top-right" | "top-left";
  onPreview: () => void;
  style?: React.CSSProperties;
}> = ({ src, alt, className, dimmed, zoomPosition, onPreview, style }) => (
  <div
    style={{ position: "relative", cursor: "pointer", ...style }}
    onClick={onPreview}
  >
    <img
      src={src}
      alt={alt}
      className={className}
      style={dimmed ? { opacity: "0.5" } : {}}
    />
    <ZoomOverlay position={zoomPosition} />
  </div>
);

const UploadButtonContainer: FC<{
  userId: string;
  onUpload: (
    userId: string,
    event: React.ChangeEvent<HTMLInputElement>
  ) => void;
}> = ({ userId, onUpload }) => (
  <div
    style={{
      marginTop: "15px",
      display: "flex",
      alignItems: "center",
      justifyContent: "space-between",
      width: "70%",
    }}
  >
    <FileUploadButton userId={userId} onUpload={onUpload} />
  </div>
);

// --- Main component ---

interface NationalIdentityCardProps {
  user: UserData;
  classes: Record<string, string>;
  hasBeenSentToWallex: boolean;
  screeningStatus: string | null;
  passportPhotoIsLoading: boolean;
  excludedFiles: string[];
  wallexPassportPhotos: Record<
    string,
    { ownerId: string; data: string; passportUserId?: string }
  >;
  regtankDocumentPhoto: RegtankPassportPhoto[];
  localPassportPhotos: Record<string, LocalPassportPhoto[]>;
  openIdentityPreview: (
    content: string,
    fileName: string,
    isPdf: boolean
  ) => void;
  toggleExcludePassportPhoto: (
    userId: string,
    frontPhoto: string,
    backPhoto: string,
    isLocal?: boolean
  ) => void;
  removeLocalPassportPhoto: (userId: string, fileIndex: number) => void;
  handlePassportPhotoUpload: (
    userId: string,
    event: React.ChangeEvent<HTMLInputElement>
  ) => void;
}

const isPhotoMatch = (
  photo: { ownerId: string; passportUserId?: string },
  userId: string
) => photo.ownerId === userId || photo.passportUserId === userId;

const NationalIdentityCard: FC<NationalIdentityCardProps> = ({
  user,
  classes,
  hasBeenSentToWallex,
  screeningStatus,
  passportPhotoIsLoading,
  excludedFiles,
  wallexPassportPhotos,
  regtankDocumentPhoto,
  localPassportPhotos,
  openIdentityPreview,
  toggleExcludePassportPhoto,
  removeLocalPassportPhoto,
  handlePassportPhotoUpload,
}) => {
  const userId = user.id.toString();

  // Fix #7: Single regtank lookup instead of two separate .find() calls
  const matchingRegtank = regtankDocumentPhoto.find(
    (x) => x.requestId === user.regtankRequest?.[0]?.requestId
  );
  const identityPhotoFrontRegtank = matchingRegtank?.frontDocumentPhoto;
  const identityPhotoBackRegtank = matchingRegtank?.backDocumentPhoto;

  const isExcluded = (photo: string) =>
    excludedFiles.includes(`user_${user.id}_photo_${photo}`);

  const areBothExcluded =
    identityPhotoFrontRegtank &&
    identityPhotoBackRegtank &&
    isExcluded(identityPhotoFrontRegtank) &&
    isExcluded(identityPhotoBackRegtank);

  const canModify =
    !hasBeenSentToWallex || screeningStatus === "documents_need_resubmission";

  // Fix #6: Use passportUserId directly — no "in" check or "as any" cast needed
  const hasWallexPhotos =
    hasBeenSentToWallex ||
    Object.values(wallexPassportPhotos).some((photo) =>
      isPhotoMatch(photo, userId)
    );

  const matchingWallexPhotos = Object.entries(wallexPassportPhotos).filter(
    ([, photo]) => isPhotoMatch(photo, userId)
  );

  const renderWallexPhoto = (
    fileName: string,
    photo: { ownerId: string; data: string },
    photoIndex: number
  ) => {
    const dataLower = photo.data?.toLowerCase() || "";
    const fileNameLower = fileName.toLowerCase();
    const isPdf =
      fileNameLower.endsWith(".pdf") ||
      dataLower.includes("application/pdf") ||
      dataLower.includes(".pdf");

    // Fix #5: Single displayName derivation, reused throughout
    const displayName =
      fileName.replace(/^\d+_/, "") || "National Identity Document (PDF)";

    return (
      <div key={photoIndex}>
        {isPdf ? (
          photo.data ? (
            <PdfPreviewCard
              displayName={displayName}
              dataUrl={photo.data}
              onPreview={() =>
                openIdentityPreview(photo.data, displayName, true)
              }
            />
          ) : (
            <Text size="sm" align="center" color="dimmed">
              {displayName}
            </Text>
          )
        ) : photo.data ? (
          <ClickablePhoto
            src={photo.data}
            alt="National Identity"
            className={classes.photo}
            onPreview={() =>
              openIdentityPreview(photo.data, displayName, false)
            }
          />
        ) : (
          <Text size="sm" align="center" color="dimmed">
            {displayName || "Document uploaded"}
          </Text>
        )}

        <div className={classes.sentToWallexBadge}>Sent to Wallex</div>
      </div>
    );
  };

  const renderLocalPhoto = (
    photoData: LocalPassportPhoto,
    fileIndex: number
  ) => (
    <div
      key={`${user.id}-${photoData.file.name}-${photoData.file.lastModified}`}
      style={{ position: "relative" }}
    >
      {photoData.file.type === "application/pdf" ? (
        <PdfPreviewCard
          displayName={photoData.file.name}
          dataUrl={photoData.previewUrl}
          border="2px dashed #ccc"
          width="200px"
          minHeight="180px"
          onPreview={() =>
            openIdentityPreview(photoData.previewUrl, photoData.file.name, true)
          }
        />
      ) : (
        <ClickablePhoto
          src={photoData.previewUrl}
          alt={`Uploaded document ${fileIndex + 1}`}
          className={classes.photo}
          zoomPosition="top-left"
          onPreview={() =>
            openIdentityPreview(
              photoData.previewUrl,
              photoData.file.name,
              false
            )
          }
        />
      )}

      {canModify && (
        <Button
          onClick={() => removeLocalPassportPhoto(userId, fileIndex)}
          variant="filled"
          color="red"
          size="xs"
          compact
          style={{ position: "absolute", top: "5px", right: "5px" }}
        >
          Remove
        </Button>
      )}
    </div>
  );

  // Fix #4: Removed redundant null checks — early return already guarantees both are truthy
  const renderRegtankPhotos = () => {
    if (
      !identityPhotoFrontRegtank ||
      !identityPhotoBackRegtank ||
      passportPhotoIsLoading
    )
      return null;

    return (
      <div style={{ position: "relative", marginBottom: "20px" }}>
        <div
          style={{
            position: "absolute",
            top: "5px",
            right: "5px",
            zIndex: 5,
          }}
        >
          <Button
            onClick={() =>
              toggleExcludePassportPhoto(
                userId,
                identityPhotoFrontRegtank,
                identityPhotoBackRegtank
              )
            }
            variant={areBothExcluded ? "outline" : "filled"}
            color={areBothExcluded ? "green" : "red"}
            size="xs"
            compact
          >
            <Text>{areBothExcluded ? "Restore" : "Exclude"}</Text>
          </Button>
        </div>
        <ClickablePhoto
          src={identityPhotoFrontRegtank}
          alt="Passport"
          className={classes.photo}
          dimmed={isExcluded(identityPhotoFrontRegtank)}
          onPreview={() =>
            openIdentityPreview(
              identityPhotoFrontRegtank,
              "National Identity (Front)",
              false
            )
          }
          style={{ marginBottom: "10px" }}
        />
        <ClickablePhoto
          src={identityPhotoBackRegtank}
          alt="Passport"
          className={classes.photo}
          dimmed={isExcluded(identityPhotoBackRegtank)}
          onPreview={() =>
            openIdentityPreview(
              identityPhotoBackRegtank,
              "National Identity (Back)",
              false
            )
          }
        />
        {areBothExcluded && (
          <div
            style={{
              position: "absolute",
              top: "50%",
              left: "50%",
              transform: "translate(-50%, -50%)",
              backgroundColor: "rgba(255, 0, 0, 0.6)",
              color: "white",
              padding: "5px 10px",
              fontWeight: "bold",
              borderRadius: "4px",
              pointerEvents: "none",
            }}
          >
            EXCLUDED
          </div>
        )}
      </div>
    );
  };

  const hasLocalPhotos =
    localPassportPhotos[user.id] && localPassportPhotos[user.id].length > 0;

  const hasRegtankOrLocalPhotos =
    (identityPhotoFrontRegtank && identityPhotoBackRegtank) ||
    localPassportPhotos[user.id];

  // Fix #3: Determine if upload button should show — used once below the ternary
  const showUploadButton = canModify && !hasWallexPhotos;

  return (
    <Card
      key={user.id}
      shadow="sm"
      padding="lg"
      radius="md"
      withBorder
      className={classes.personCard}
      style={{ marginBottom: "15px" }}
    >
      <Card.Section withBorder inheritPadding py="xs" mb="xs">
        <Group position="apart">
          <Text weight={500}>{user?.firstName + " " + user?.lastName}</Text>
          <Text weight={200}>{user?.jobTitle || " "}</Text>
        </Group>
      </Card.Section>
      <LoadingOverlay visible={passportPhotoIsLoading} />
      <div className={classes.photoDisplay}>
        {hasWallexPhotos ? (
          <div style={{ position: "relative" }}>
            {matchingWallexPhotos.length > 0 ? (
              <div>
                {matchingWallexPhotos.map(([fileName, photo], photoIndex) =>
                  renderWallexPhoto(fileName, photo, photoIndex)
                )}
              </div>
            ) : (
              <Text color="dimmed" italic align="center" py="md">
                No National Identity Document has been sent.
              </Text>
            )}
          </div>
        ) : hasRegtankOrLocalPhotos ? (
          <>
            {renderRegtankPhotos()}
            {hasLocalPhotos && (
              <div
                style={{
                  marginTop: user?.didPassportPhoto ? "10px" : "0",
                  display: "flex",
                  flexWrap: "wrap",
                  gap: "10px",
                }}
              >
                {localPassportPhotos[user.id].map((photoData, fileIndex) =>
                  renderLocalPhoto(photoData, fileIndex)
                )}
              </div>
            )}
          </>
        ) : (
          !hasBeenSentToWallex && (
            <Text color="dimmed" italic align="center" py="md">
              No National Identity Document available
            </Text>
          )
        )}

        {/* Fix #3: Upload button hoisted out of ternary branches */}
        {showUploadButton && (
          <UploadButtonContainer
            userId={userId}
            onUpload={handlePassportPhotoUpload}
          />
        )}
      </div>
    </Card>
  );
};

export default NationalIdentityCard;
